<template>
  <div class="shipment-rate-shop-tab" :class="tab.key">
    <RateShopBenchmarkRow
      v-model:freight-allowance="freightAllowanceInternal"
      :benchmarks="benchmarks"
      :rate-shop="rateShop"
      :show-freight-allowance="shouldShowFreightAllowance"
      :freight-allowance-editable="isFreightAllowanceEditable"
    />

    <Message
      v-if="
        waterfallTender?.status ===
        WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE
      "
      severity="error"
    >
      <div class="flex flex-gap-1 align-items-center">
        <span>
          {{ t('waterfallTender.finishedWithoutAcceptedQuoteWarning') }}
        </span>

        <RygenButton
          mode="tertiary"
          :label="
            t('common.cancelParam', {
              param: t('waterfallTender.waterfallTender'),
            })
          "
          :severity="Severity.DANGER"
          :style="{ flex: '1 0 auto' }"
          class="flex-grow-1"
          icon="pi pi-times"
          @click="
            emit('command', { action: ShipmentAction.CANCEL_WATERFALL_TENDER })
          "
        />
      </div>
    </Message>

    <WaterfallTenderTable
      id="waterfall-tender-table"
      :rate-quotes="waterfallTender?.quotes ?? []"
      :loading="rateShopLoading"
      @edit-quote="emit('edit-quote', $event)"
      @edit-spot-quote="emit('edit-spot-quote', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { ActionCommand, Loadable, Severity } from '@rygen/rygen-ui/model';

import { useFreightAllowance, useRateShop } from '@/composables';
import {
  FreightAllowance,
  RateQuote,
  RateShop,
  Shipment,
  ShipmentAction,
  WaterfallTender,
  WaterfallTenderStatus,
} from '@/model';

import RateShopBenchmarkRow from '@/components/rateShop/RateShopBenchmarkRow.vue';
import WaterfallTenderTable from '@/components/rateShop/WaterfallTenderTable.vue';
import { ShipmentRateShopTab } from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Message from 'primevue/message';

const props = defineProps({
  tab: {
    type: Object as PropType<ShipmentRateShopTab>,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  waterfallTender: {
    type: Object as PropType<WaterfallTender | undefined>,
    default: () => undefined,
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
});
const {
  tab,
  shipment,
  rateShop,
  rateShopLoading,
  waterfallTender,
  freightAllowance,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'edit-spot-quote', rateQuote: RateQuote): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const { benchmarks } = useRateShop(rateShop, {
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});
</script>
